import * as yup from 'yup';
import { useMutation } from '@tanstack/react-query';
import { format } from 'date-fns';
import {
  getTimeBlockForm,
  TimeBlockFormValues,
} from '../../types/availability';
import useUser from '../global/useUser';
import {
  convertDateToOnschedHours,
  isNil,
  isUserAdminOrManager,
} from '../../utils/global/helper';
import { createTimeBlock, updateTimeBlock } from '../../services/employee';
import { createShopTimeBlocks } from '../../services/business';
import { ResourceBlock } from '../../types/employees';

interface Options {
  onSuccess?: () => void;
  onError?: (error: Error) => void;
}

function useCreateUpdateTimeBlock({ onSuccess, onError }: Options = {}) {
  const { user } = useUser();

  const timeBlockForm = getTimeBlockForm(user);

  return useMutation({
    mutationFn: async (body: TimeBlockFormValues) => {
      try {
        if (!user?.business?.id) return false;

        const validatedPayload = await timeBlockForm
          .noUnknown()
          .validate(body, {
            stripUnknown: true,
          });

        if (
          !validatedPayload.isAllDay &&
          (!validatedPayload.startTime || !validatedPayload.endTime)
        ) {
          throw new yup.ValidationError(
            'Start and end time are required',
            body,
            'startTime',
          );
        }

        const startTimeNumber = validatedPayload.isAllDay
          ? 0
          : convertDateToOnschedHours(validatedPayload.startTime!);
        let endTimeNumber = validatedPayload.isAllDay
          ? 2400
          : convertDateToOnschedHours(validatedPayload.endTime!);
        if (endTimeNumber === 0) {
          endTimeNumber = 2400;
        }

        const asManager =
          user?.business.type === 'STUDIO' &&
          isUserAdminOrManager(user) &&
          validatedPayload.employeeIds?.some(id => id !== user.userId);
        const employeeIds = (
          asManager ? validatedPayload.employeeIds ?? [] : [user.userId]
        ).filter(Boolean) as string[];

        if (!employeeIds?.length) {
          throw new yup.ValidationError(
            'Please select at least one employee',
            employeeIds,
            'employeeIds',
          );
        }

        const payload: ResourceBlock = validatedPayload.repeats
          ? {
              startDate: format(validatedPayload.dateRange?.[0]!, 'yyyy-MM-dd'),
              endDate: format(validatedPayload.dateRange?.[1]!, 'yyyy-MM-dd'),
              startTime: startTimeNumber,
              endTime: endTimeNumber,
              reason: validatedPayload.name,
              repeats: true,
              repeat: {
                frequency: validatedPayload.repeats,
                interval: validatedPayload.interval ?? 1,
              },
            }
          : {
              startDate: format(validatedPayload.date!, 'yyyy-MM-dd'),
              endDate: format(validatedPayload.date!, 'yyyy-MM-dd'),
              startTime: startTimeNumber,
              endTime: endTimeNumber,
              reason: validatedPayload.name,
              repeats: false,
            };

        if (
          validatedPayload.repeats === 'W' &&
          payload.repeat &&
          validatedPayload.days
        ) {
          payload.repeat.weekdays = [...validatedPayload.days].sort().join('');
        }

        const isUpdating = !isNil(validatedPayload.resourceBlockId);

        if (asManager && !isUpdating) {
          const result = await createShopTimeBlocks(user.business.id, {
            ...payload,
            employeeIds,
          });
          return !!result?.length;
        }

        const result = isUpdating
          ? await updateTimeBlock(validatedPayload.resourceBlockId!, {
              ...payload,
              employeeId: validatedPayload.employeeId!,
            })
          : await createTimeBlock(user.userId, payload);
        return !!result;
      } catch (error) {
        if (error instanceof yup.ValidationError) {
          throw new Error(error.message);
        }
        throw new Error('Failed to create time block');
      }
    },
    onSuccess: result => {
      if (!result) return;
      onSuccess?.();
    },
    onError,
  });
}

export default useCreateUpdateTimeBlock;
